import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

import type { RoomFloorplanDto } from '../generated/contracts';
import { buildLabelledPlate, buildMultiLinePlate, disposePlate, repaintMultiLinePlate } from '../input/LabelledPlate';

/**
 * The admin-only error/notice log (plan/in-room-signage.md): a running list of the app's most
 * recent errors and notices, newest first, on the south wall to the left of `DebugPanel` as
 * seen by someone standing in the room facing that wall. Diagnostic scaffolding, not a product
 * surface — the owner's own framing: "When this is debugged properly I hope we won't need it,
 * but it will at least give me somewhere to look if something is weird."
 *
 * **Visibility matches `DebugPanel` exactly** — `enabled` is the caller's own
 * `currentAccount.username === 'lars'` check (`main.ts`), the identical gate `DebugPanel`
 * already uses, not a second, differently-scoped check invented for this feature
 * (plan/in-room-signage.md: `OrgRoles` has only `Owner`/`Member` and calls itself "nothing
 * resembling a permission system," so gating on `Owner` would show this to every org's owner).
 * When `enabled` is false, no meshes are ever built — `record()` becomes a cheap no-op rather
 * than keeping a ring buffer no session will ever render, the same "nothing to accidentally
 * render or aim at" discipline `DebugPanel`'s own constructor doc comment describes.
 *
 * **Which side of `DebugPanel` is "left."** `DebugPanel` sits at `x = -PANEL_X_OFFSET_METRES`
 * (`DebugPanel.ts`'s own comment: "how far *west* of room-centre"), which is `-X` only relative
 * to the room's own default facing — `SceneRoot.spawnPoint`/`spawnYaw`, "just inside the south
 * wall, looking across the room" at `+Z`. Nobody reads `DebugPanel` while facing `+Z`, though:
 * it is mounted on the south wall, so reading it means having turned around to face `-Z`
 * instead. A 180-degree turn swaps which physical hand each world axis maps to — a body facing
 * `+Z` has its right hand toward `+X`; the identical hand, once that body turns to face `-Z`,
 * points toward `-X` instead (the hand did not move relative to the body; only the body's own
 * facing did). So `DebugPanel`, sitting at `-X`, is on that viewer's *right*, not their left,
 * despite `-X` being the "west"/negative side. This panel is the one asked to sit at the
 * viewer's *left* while facing the south wall, which is therefore the *positive*-X side — the
 * mirror image of `DebugPanel`'s own offset, `+PANEL_X_OFFSET_METRES` rather than `-`, using
 * the identical magnitude and the identical `PANEL_SIDE_MARGIN`-based clamp
 * (`DebugPanel.reposition`'s own comment) so the two scale down together for a narrower
 * template rather than drifting apart from two different formulas.
 *
 * **Non-overlap.** With both panels clamped by the same formula, their centres sit at `-offset`
 * and `+offset` for a shared `offset` in `[0, maxOffset]` — `2 * offset` apart. `DebugPanel`'s
 * own widest row (five trim swatches, `SWATCH_SPACING`/`SWATCH_WIDTH`) is 0.44m, half-width
 * 0.22m; this panel's own widest row (`LOG_WIDTH`/`TITLE_WIDTH`, both 0.5m) is half-width 0.25m.
 * The two cannot touch until `offset < (0.22 + 0.25) / 2 = 0.235`, i.e. `maxOffset < 0.235`,
 * i.e. a template narrower than roughly 1.1m wide (`PANEL_SIDE_MARGIN` is 0.3m either side of
 * that). No template this project ships gets remotely that narrow — `WhiteboardLayout.ts`'s own
 * `BOARD_MIN_WIDTH` (1.2m) already assumes more room than that for the whiteboard alone — so
 * this is the same class of degenerate edge case `DebugPanel`'s own `maxOffset = 0` collapse
 * already accepts without a guard, not a new gap this panel introduces.
 *
 * **South-wall yaw.** Same requirement as `DebugPanel`: `buildLabelledPlate`/
 * `buildMultiLinePlate`'s planes front -Z by construction, and the south wall's own
 * inward-facing side is +Z, so the root needs the identical `Math.PI` yaw
 * (`DebugPanel.reposition`'s own doc comment has the full derivation).
 */

/** Matches `DebugPanel.ts`'s own identical constants — restated per this project's usual
 *  "cannot import a private number" reasoning. */
const WALL_THICKNESS = 0.1;
const WALL_CLEARANCE = 0.01;

/** Matches `DebugPanel.ts`'s own `PANEL_HEIGHT_METRES` — the same eye-level height, so the two
 *  panels read as a matched pair on the same wall. */
const PANEL_HEIGHT_METRES = 1.5;

/** The mirror of `DebugPanel.ts`'s own `PANEL_X_OFFSET_METRES`/`PANEL_SIDE_MARGIN` — see this
 *  file's own module comment for the sign derivation and the non-overlap proof. */
const PANEL_X_OFFSET_METRES = 1.6;
const PANEL_SIDE_MARGIN = 0.3;

const TITLE_WIDTH = 0.5;
const TITLE_HEIGHT = 0.06;

const LOG_WIDTH = 0.5;

/**
 * Fixed cap on remembered lines — "plain and cheap," the owner's own framing
 * (plan/in-room-signage.md). Eight is enough to show a genuine pattern (the same failure
 * recurring) rather than only the single most recent message, which alone cannot distinguish
 * "one weird thing happened" from "this keeps happening" — while staying one small texture
 * repainted in place (`repaintMultiLinePlate`), not a scrollable history view.
 */
const MAX_LINES = 8;

const LINE_HEIGHT = 0.045;
const LOG_HEIGHT = LINE_HEIGHT * MAX_LINES;

/** Gap between the title plate and the log plate. */
const TITLE_GAP = 0.02;

const BACKGROUND = '#12151A';
const TITLE_INK = '#8A93A6';
const LOG_INK = '#E7ECF3';

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export class ErrorLogPanel {
  private readonly root: TransformNode;
  private title: Mesh | null = null;
  private log: Mesh | null = null;
  private positioned = false;

  /** Newest first, capped at `MAX_LINES` — see `record()`. */
  private readonly lines: string[] = [];

  /**
   * @param enabled Whether this browser's signed-in user is `lars` — read once, by the caller,
   *   the identical check `DebugPanel` uses (see this class's own doc comment). When false, no
   *   meshes are ever built and `record()` is a no-op.
   */
  constructor(
    private readonly scene: Scene,
    private readonly enabled: boolean,
  ) {
    this.root = new TransformNode('error-log-panel', scene);
    this.root.setEnabled(false);

    if (!this.enabled) {
      return;
    }

    this.buildContents();
  }

  /** Whether the panel is currently positioned and shown — false before the first
   *  `reposition()` call, and always false for a disabled panel. */
  get isVisible(): boolean {
    return this.enabled && this.positioned;
  }

  /** How many lines are currently recorded, capped at `MAX_LINES` — exposed for tests. */
  get lineCount(): number {
    return this.lines.length;
  }

  /**
   * Places the panel on the south wall, to the left of `DebugPanel`, for a room of this shape —
   * see this file's own module comment for the geometry and the non-overlap proof. Safe to
   * call repeatedly, the same "reproduces the same transform for a same-shaped floorplan"
   * contract `DebugPanel.reposition()` documents for itself. A no-op for a disabled panel.
   */
  reposition(floorplan: RoomFloorplanDto): void {
    if (!this.enabled) {
      return;
    }

    const halfWidth = floorplan.width / 2;
    const halfDepth = floorplan.depth / 2;

    const wallInnerZ = -halfDepth + WALL_THICKNESS / 2;
    const mountZ = wallInnerZ + WALL_CLEARANCE;

    const maxOffset = Math.max(0, halfWidth - PANEL_SIDE_MARGIN);
    const x = clampNumber(PANEL_X_OFFSET_METRES, 0, maxOffset);

    const y = Math.min(PANEL_HEIGHT_METRES, Math.max(0.6, floorplan.wallHeight - 0.6));

    this.root.position.set(x, y, mountZ);
    this.root.rotationQuaternion = Quaternion.FromEulerAngles(0, Math.PI, 0);
    this.root.setEnabled(true);
    this.positioned = true;
  }

  /**
   * Records a message, newest first, dropping the oldest past `MAX_LINES` — called by
   * `main.ts` for every one of `showNotice`'s existing producers, plus `window.onerror`,
   * `unhandledrejection`, and hub-connection drops (plan/in-room-signage.md, "What feeds it").
   * A no-op for a disabled panel, so a non-`lars` session never keeps this array around at all.
   */
  record(message: string): void {
    if (!this.enabled) {
      return;
    }

    this.lines.unshift(message);
    this.lines.length = Math.min(this.lines.length, MAX_LINES);

    if (this.log) {
      repaintMultiLinePlate(this.log, this.lines, BACKGROUND, LOG_INK);
    }
  }

  private buildContents(): void {
    const title = buildLabelledPlate(
      this.scene,
      'error-log-panel-title',
      'errors & notices (lars)',
      BACKGROUND,
      TITLE_INK,
      TITLE_WIDTH,
      TITLE_HEIGHT,
    );
    title.position.set(0, LOG_HEIGHT / 2 + TITLE_GAP + TITLE_HEIGHT / 2, 0);
    title.parent = this.root;
    title.isPickable = false;
    this.title = title;

    const log = buildMultiLinePlate(
      this.scene,
      'error-log-panel-log',
      this.lines,
      BACKGROUND,
      LOG_INK,
      LOG_WIDTH,
      LOG_HEIGHT,
    );
    log.position.set(0, 0, 0);
    log.parent = this.root;
    log.isPickable = false;
    this.log = log;
  }

  /**
   * `root.dispose()` alone would cascade into both meshes but leave their materials and
   * textures alive (CLAUDE.md, "That same cascade does not free materials") — the identical
   * reasoning `DebugPanel.dispose()`'s own doc comment gives, restated here for the same reason
   * `RoomPlaque.dispose()` restates it too.
   */
  dispose(): void {
    if (this.title) {
      disposePlate(this.title);
      this.title = null;
    }

    if (this.log) {
      disposePlate(this.log);
      this.log = null;
    }

    this.root.dispose();
  }
}
